import { Injectable } from '@angular/core';
import { AlternateApi } from '../constants-types/AlternateApi';
import { englishLanguageLocale } from '../constants-types/constants';
import { PI } from '../constants-types/PI.model';

// NOTE: This is located in the @Translations lib because of dependency reasons.
// The @Translations lib should be relatively free of dependencies on other services.
// This is one of the few ones that is required so it was easiest to stuff it in here.

/*
This is an injectable service that serves as a wrapper around any configuration
you have. This is intended to read from objected loaded via script tags before
the application is running.
*/

/*
This is built in an unfortunate way due to the type system. It would be nice
to find a more dynamic way of building this that both hides the global
configuration object while at the same time exposing all the properties on it
without having to write code here. A puzzle for another day.
*/

@Injectable({ providedIn: 'root' })
export class ConfigurationService {
    get currentLocale() {
        return PI.languageLocale;
    }

    getClassicURL() {
        return PI.appSettings.apiUrl;
    }

    getAlternateAPIs(): AlternateApi[] {
        return PI.appSettings.alternateApiUrls || [];
    }

    getSignalRUrl(): string {
        return PI.appSettings.signalRUrl;
    }

    getUrlWithHost(url: string) {
        const altHost = this.getAlternateAPIs().filter((alt) =>
            url.includes(alt.altPath)
        )[0];
        return (altHost ? altHost.altUrl : this.getClassicURL()) + url;
    }

    getAllowedHostList(): string[] {
        return [
            this.getHostFromUrl(PI.appSettings.apiUrl),
            ...this.getAlternateAPIs().map((alt) =>
                this.getHostFromUrl(alt.altUrl)
            ),
            ...this.arrayIfNotFalsy(
                PI.appSettings.designApiUrl,
                PI.appSettings.userApiUrl,
                PI.appSettings.accountManagementApiUrl,
                PI.appSettings.searchApiUrl,
                PI.appSettings.integrationsApiUrl,
                PI.appSettings.orgApiUrl,
                PI.appSettings.dataImportApiUrl,
                PI.appSettings.companySettingsApiUrl,
                PI.appSettings.demoAccountsApiUrl,
                PI.appSettings.integrationsAdminApiUrl,
                PI.appSettings.employeeAccessApiUrl,
                PI.appSettings.upgradeApiUrl
            )
        ];
    }

    arrayIfNotFalsy(...urls: string[]): string[] {
        const allowList = [];
        urls.forEach((url) => {
            if (url) {
                allowList.push(this.getHostFromUrl(url));
            }
        });
        return allowList;
    }

    getCopyrightYear() {
        return new Date().getFullYear();
    }

    getOpenInvitationBaseUrl() {
        return PI.appSettings.openInvitationBaseUrl;
    }

    getMarketingPageUrl() {
        return PI.appSettings.marketingPageUrl;
    }

    getAuth0Settings() {
        return PI.appSettings.auth0;
    }

    getForceBCNetworkUrl() {
        return PI.appSettings.networkForceBCUrl;
    }

    getForceBCHQUrl() {
        return PI.appSettings.hqForceBCUrl;
    }

    getFeatureFlagKey() {
        return PI.appSettings.featureFlagKey;
    }

    getLaunchDarklyEnvironmentKey() {
        return PI.appSettings.launchDarklyEnvironmentKey;
    }

    getLaunchDarklyFlushIntervalInMilliSeconds() {
        return PI.appSettings.launchDarklyFlushIntervalInMilliSeconds;
    }

    getLogRocketEnabled() {
        return PI.appSettings.logRocketEnabled;
    }

    isEnglish() {
        return this.currentLocale === englishLanguageLocale;
    }

    getLoggingServiceAPIUrl() {
        return PI.appSettings.loggingServiceAPIUrl;
    }

    getJobBenchmarkAssociatedPersonLimit() {
        return PI.appSettings.jobBenchmarkAssociatedPersonLimit;
    }

    getCompanyGuidsWithMaskedCogData(): string[] {
        return PI.appSettings.cogDataMaskedCompanyGuids;
    }

    private getHostFromUrl(url: string): string {
        return new URL(url).host;
    }
}
